<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { NIcon } from 'naive-ui';
import { IconAdd, IconChevronForward, IconFolder, IconHome } from '@/components/design/icons';
import { createVaultDirectory } from '@/api';
import { fetchWholeDirectory, itemsOfKind } from '@/services/vaultListingService';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import {
  gaiaAccent,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
} from '@/components/design/themes/tokens';
import { useVaultWebSocket } from '@/composables/useVaultWebSocket';
import { extractErrorDetail } from '@/utils/errorHandler';
import type { VaultFolderPickerLocation } from './vaultFolderPicker';

interface ChildDirectory {
  uid: string;
  name: string;
}

interface BreadcrumbEntry {
  uid: string;
  label: string;
}

/**
 * Browses the main vault one folder level at a time, with a breadcrumb trail
 * back up. The folder you are in is the pick, reported through
 * `update:location`; what counts as a valid pick (write access, not the root,
 * not the folder being moved) is the caller's. Starts at the main vault's
 * root, so system-vault folders are never offered — or, with `rootUid`, at
 * that folder, which it then can't climb above.
 */
const props = withDefaults(
  defineProps<{
    /** Reset to the root whenever this turns true (a dialog opening); live updates only run while true. */
    active?: boolean;
    /** Folders that can't be entered (e.g. the folders being moved). */
    disabledUids?: string[];
    /** Tooltip on a disabled folder. */
    disabledTitle?: string;
    /** Offer "New folder" inside the current folder; the new folder becomes the pick. */
    allowCreate?: boolean;
    /** Freezes navigation (e.g. while the caller submits). */
    disabled?: boolean;
    /** Body of the empty state when the current folder has no subfolders. */
    emptyHint?: string;
    /** Folder the picker starts at and can't climb above (e.g. a data room); default the vault root. */
    rootUid?: string;
    /** The first crumb's label when `rootUid` is set. */
    rootLabel?: string;
  }>(),
  {
    rootUid: undefined,
    rootLabel: 'Folder',
    active: true,
    disabledUids: () => [],
    disabledTitle: '',
    allowCreate: false,
    disabled: false,
    emptyHint: 'This folder has no subfolders.',
  }
);

const emit = defineEmits<{
  'update:location': [location: VaultFolderPickerLocation | null];
}>();

const breadcrumbs = ref<BreadcrumbEntry[]>([]);
const currentUid = ref<string | null>(null);
/** The main vault's root; stays unknown in a scoped picker, which never reaches it. */
const vaultRootUid = ref<string | null>(null);
/** Bumped by every navigation, so a load that finishes late can tell it was overtaken. */
let navigation = 0;
/** Owner of the currently open folder — writing into it requires write access. */
const children = ref<ChildDirectory[]>([]);
const loading = ref(false);
const errorMessage = ref<string>('');

const creatingFolder = ref(false);
const savingFolder = ref(false);
const newFolderName = ref('');
const newFolderInput = ref<{ focus: () => void } | null>(null);

const disabledUidSet = computed<Set<string>>(() => new Set(props.disabledUids));

// `currentUid` and the trail change together once a folder has loaded (see
// loadDirectory), so the pick never pairs one folder's uid with another's name
// and never flickers to null mid-navigation.
const location = computed<VaultFolderPickerLocation | null>(() => {
  if (!currentUid.value) return null;
  return {
    uid: currentUid.value,
    label: breadcrumbs.value.at(-1)?.label ?? '',
    isRoot: currentUid.value === vaultRootUid.value,
  };
});

watch(
  location,
  value => {
    emit('update:location', value);
  },
  { immediate: true }
);

const busy = computed(() => loading.value || savingFolder.value || props.disabled);

function resetState(): void {
  breadcrumbs.value = [];
  currentUid.value = null;
  children.value = [];
  errorMessage.value = '';
  loading.value = false;
  cancelNewFolder();
}

/**
 * Load a folder's children. `trail` is the breadcrumb trail ending at that
 * folder; it is applied together with `currentUid` once the load succeeds, so
 * a failed load leaves the picker where it was. Omit it to refresh in place.
 */
async function loadDirectory(
  uid: string | null,
  trail?: BreadcrumbEntry[] | ((loadedUid: string) => BreadcrumbEntry[])
): Promise<void> {
  // Loads overlap: creating a folder navigates into it while the websocket
  // reports its parent as changed and refreshes that. Only the latest
  // navigation may move the picker, and a refresh only lands on the folder
  // still open — else the trail shows one folder and the list another.
  if (trail) navigation += 1;
  const token = navigation;
  loading.value = true;
  errorMessage.value = '';
  try {
    // The whole folder: its subfolders may sit on any page of the listing.
    const directory = await fetchWholeDirectory(uid ?? undefined);
    if (token !== navigation) return;
    if (!trail && directory.uid !== currentUid.value) return;
    if (!uid) vaultRootUid.value = directory.uid;
    if (trail) breadcrumbs.value = typeof trail === 'function' ? trail(directory.uid) : trail;
    currentUid.value = directory.uid;
    children.value = itemsOfKind(directory.items, 'directory').map(c => ({
      uid: c.uid,
      name: c.directory_name,
    }));
  } catch (error) {
    if (token !== navigation) return;
    errorMessage.value = extractErrorDetail(error, 'Failed to load directory.');
    children.value = [];
  } finally {
    if (token === navigation) loading.value = false;
  }
}

async function openRoot(): Promise<void> {
  resetState();
  // A scoped picker's top is the given folder; there is no crumb above it.
  if (props.rootUid) {
    await loadDirectory(props.rootUid, [{ uid: props.rootUid, label: props.rootLabel }]);
    return;
  }
  // The root's uid is only known from the response; the label is ours.
  await loadDirectory(null, loadedUid => [{ uid: loadedUid, label: 'My Vault' }]);
}

watch(
  () => props.active,
  active => {
    if (active) {
      void openRoot();
    } else {
      resetState();
    }
  },
  { immediate: true }
);

// --- Live vault updates over the websocket ---
// Only watch while active; returning [] reconciles the subscriptions away.

useVaultWebSocket({
  directoryUids: () => {
    if (!props.active) return [];
    const uids = breadcrumbs.value.map(entry => entry.uid);
    if (currentUid.value) {
      uids.push(currentUid.value);
    }
    return Array.from(new Set(uids));
  },
  currentDirectoryUid: () => (props.active ? currentUid.value : null),
  onCurrentDirectoryUpdated: () => {
    void loadDirectory(currentUid.value);
  },
  onParentDirectoryUpdated: (uid, name) => {
    const crumb = breadcrumbs.value.find(entry => entry.uid === uid);
    if (crumb) {
      crumb.label = name;
    }
  },
  onCurrentOrParentDeleted: () => {
    void openRoot();
  },
});

function isChildDisabled(child: ChildDirectory): boolean {
  return disabledUidSet.value.has(child.uid);
}

async function enterChild(child: ChildDirectory): Promise<void> {
  if (isChildDisabled(child)) return;
  cancelNewFolder();
  await loadDirectory(child.uid, [...breadcrumbs.value, { uid: child.uid, label: child.name }]);
}

async function jumpToCrumb(index: number): Promise<void> {
  const target = breadcrumbs.value.at(index);
  if (!target) return;
  cancelNewFolder();
  await loadDirectory(target.uid, breadcrumbs.value.slice(0, index + 1));
}

async function startNewFolder(): Promise<void> {
  newFolderName.value = '';
  creatingFolder.value = true;
  await nextTick();
  newFolderInput.value?.focus();
}

// One keydown listener, not `@keydown.enter` + `@keydown.esc`: two listeners
// merge into an array, which GaiaInput forwards as n-input's `onKeydown` prop —
// typed as a single function.
function handleNewFolderKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter') {
    event.preventDefault();
    void saveNewFolder();
  } else if (event.key === 'Escape') {
    // Esc cancels the new folder, not the dialog around the picker.
    event.stopPropagation();
    cancelNewFolder();
  }
}

function cancelNewFolder(): void {
  creatingFolder.value = false;
  newFolderName.value = '';
}

// Created straight through the API rather than vaultStore.createDirectory,
// which would refresh whatever directory the vault view is showing.
async function saveNewFolder(): Promise<void> {
  const name = newFolderName.value.trim();
  if (!name || !currentUid.value || savingFolder.value) return;
  savingFolder.value = true;
  errorMessage.value = '';
  try {
    const res = await createVaultDirectory({
      body: { directory_name: name, parent_directory_uid: currentUid.value },
    });
    if (!res.data) throw new Error('Failed to create folder.');
    const created = res.data;
    cancelNewFolder();
    await loadDirectory(created.uid, [
      ...breadcrumbs.value,
      { uid: created.uid, label: created.directory_name },
    ]);
  } catch (error) {
    errorMessage.value = extractErrorDetail(error, 'Failed to create folder.');
  } finally {
    savingFolder.value = false;
  }
}

defineExpose({ reload: () => loadDirectory(currentUid.value) });

const accentColor = gaiaAccent.base;
// Bound from the template (an icon colour), so it stays a plain const.
const faintColor = gaiaNeutral.textFaint;

/**
 * Inline custom properties rather than `v-bind()` in the style block: callers
 * mount this inside a GaiaModal, whose Teleport root leaves `v-bind()` vars
 * with no element to land on.
 */
const styleVars = {
  '--picker-crumb-hover': gaiaNeutral.surfaceHover,
  '--picker-crumb-size': gaiaFontSize.control,
  '--picker-current-weight': String(gaiaFontWeight.semibold),
  '--picker-faint-color': gaiaNeutral.textFaint,
  '--picker-ink-color': gaiaNeutral.ink,
  '--picker-panel-border': gaiaNeutral.borderCard,
  '--picker-row-border': gaiaNeutral.borderSoft,
  '--picker-row-hover': gaiaNeutral.surfaceSoft,
  '--picker-row-size': gaiaFontSize.row,
  '--picker-subtle-color': gaiaNeutral.textSubtle,
};
</script>

<template>
  <n-flex vertical :size="12" :wrap="false" :style="styleVars">
    <n-flex align="center" justify="space-between" :size="8" :wrap="false">
      <!-- The trail you are browsing. The last crumb is the pick, so it is
           stated rather than clickable. -->
      <n-flex align="center" :size="2" class="crumbs">
        <template v-for="(crumb, index) in breadcrumbs" :key="crumb.uid">
          <button
            type="button"
            class="crumb"
            :class="{ current: index === breadcrumbs.length - 1 }"
            :disabled="index === breadcrumbs.length - 1 || busy"
            @click="jumpToCrumb(index)"
          >
            <n-icon v-if="index === 0" :component="IconHome" :size="13" />
            {{ crumb.label }}
          </button>
          <n-icon
            v-if="index < breadcrumbs.length - 1"
            :component="IconChevronForward"
            :size="11"
            :color="faintColor"
          />
        </template>
      </n-flex>
      <gaia-button
        v-if="allowCreate && !creatingFolder"
        size="tiny"
        quaternary
        :disabled="busy || !currentUid"
        @click="startNewFolder"
      >
        <template #icon><n-icon :component="IconAdd" /></template>
        New folder
      </gaia-button>
    </n-flex>

    <n-flex v-if="creatingFolder" align="center" :size="8" :wrap="false">
      <gaia-input
        ref="newFolderInput"
        v-model:value="newFolderName"
        size="small"
        placeholder="Folder name"
        :disabled="savingFolder"
        @keydown="handleNewFolderKeydown"
      />
      <gaia-button size="small" :disabled="savingFolder" @click="cancelNewFolder">
        Cancel
      </gaia-button>
      <gaia-button
        size="small"
        type="primary"
        :loading="savingFolder"
        :disabled="!newFolderName.trim()"
        @click="saveNewFolder"
      >
        Create
      </gaia-button>
    </n-flex>

    <div class="panel">
      <n-flex v-if="loading" align="center" justify="center" class="panel-state">
        <gaia-spinner size="small" />
      </n-flex>

      <gaia-empty-state v-else-if="children.length === 0" title="No subfolders here">
        {{ emptyHint }}
      </gaia-empty-state>

      <div v-else class="folders">
        <button
          v-for="child in children"
          :key="child.uid"
          type="button"
          class="folder"
          :disabled="isChildDisabled(child) || busy"
          :title="isChildDisabled(child) ? disabledTitle : ''"
          @click="enterChild(child)"
        >
          <n-icon :component="IconFolder" :size="17" :color="accentColor" class="folder-icon" />
          <span class="folder-name">{{ child.name }}</span>
          <n-icon :component="IconChevronForward" :size="13" :color="faintColor" />
        </button>
      </div>
    </div>

    <gaia-banner v-if="errorMessage" type="error">{{ errorMessage }}</gaia-banner>
  </n-flex>
</template>

<style scoped>
/* The trail and folder rows are plain buttons: naive has no breadcrumb-as-
   picker or list-row-button with these states, so their look lives here. */
.crumbs {
  flex-wrap: wrap;
  min-width: 0;
}

.crumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 8px;
  border: none;
  border-radius: 7px;
  background: none;
  font-family: inherit;
  font-size: var(--picker-crumb-size);
  color: var(--picker-subtle-color);
  cursor: pointer;
}

.crumb:hover:not(:disabled) {
  background: var(--picker-crumb-hover);
  color: var(--picker-ink-color);
}

/* The pick — stated, not a control. */
.crumb.current {
  color: var(--picker-ink-color);
  font-weight: var(--picker-current-weight);
  cursor: default;
}

.panel {
  min-height: 180px;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid var(--picker-panel-border);
  border-radius: 10px;
}

.panel-state {
  padding: 60px 0;
}

.folders {
  display: flex;
  flex-direction: column;
}

.folder {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-bottom: 1px solid var(--picker-row-border);
  background: none;
  font-family: inherit;
  font-size: var(--picker-row-size);
  color: var(--picker-ink-color);
  text-align: left;
  cursor: pointer;
}

.folder:last-child {
  border-bottom: none;
}

.folder:hover:not(:disabled) {
  background: var(--picker-row-hover);
}

/* A folder you cannot enter — e.g. the subtree being moved. */
.folder:disabled {
  color: var(--picker-faint-color);
  cursor: not-allowed;
}

.folder-icon {
  flex: none;
}

.folder-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
